/**
 * Holds a viewport's left/right alignment in place - the "& Lock" half of a
 * hanging protocol's alignment, and the Rotation menu's Lock Left / Lock Right.
 *
 * Alignment on its own is a one-off write: setStackPosition pins the image's edge
 * to the canvas edge, and then anything that moves the camera - the Pan tool, a
 * middle-drag, a propagated action, a zoom - slides it away again. A lock is that
 * same alignment plus a standing correction: the horizontal pan the alignment left
 * behind is remembered, and every camera change that departs from it is undone on
 * the spot.
 *
 * Horizontal only. What the lock is about is which side of the viewport the image
 * sits on, so panning up and down stays the radiologist's - on a tall image that
 * is the whole point of panning, and taking it away would make a locked viewport
 * unreadable rather than tidy. A sideways drag is refused; the vertical half of
 * the same drag goes through.
 *
 * Zoom is not undone either, only re-pinned. Scaling happens about the canvas
 * centre, so a left-aligned image drifts by half the canvas times the zoom applied
 * - the same sum repinAlignmentAfterZoom does for a protocol's own zoom. A locked
 * viewport can still be zoomed, windowed and scrolled.
 *
 * That re-pin is arithmetic, though, and arithmetic only holds for a zoom about
 * the canvas centre. resetCamera - which Reset, Fit to screen, Original and a
 * resize all go through - refits AND recentres, so the sum runs away and the
 * image lands off-canvas. The repair is DISPLAY_AREA_MODIFIED: cornerstone
 * re-applies the stored displayArea at the end of those operations, with camera
 * events suppressed and starting from the fit camera, which both discards
 * whatever this file wrote on the way past and leaves the image genuinely flush
 * against its edge. So that event is taken as the truth and the remembered
 * position re-read from it.
 */

import { Enums } from "@cornerstonejs/core";
import { alignmentPanDirection, getViewport } from "./CornerstoneViewportService";

// Sub-pixel camera jitter is not a pan. Correcting it would fire a render on
// every mouse move for no visible change.
const PAN_EPSILON = 0.5;

/**
 * The live locks, by viewport index, so one can be lifted the instant something
 * decides to rather than on React's next commit.
 *
 * Everything that takes a lock off - Align Left/Center/Right, Reset, the padlock
 * - goes on to write the camera itself, usually in the same tick. Waiting for
 * the state change to unmount the effect would leave the listener attached
 * across that write, and it would drag the image straight back.
 */
const releases = new Map();

/** Lift the lock on one viewport now. A no-op if it is not locked. */
export const releaseAlignmentLock = (viewportIndex) => {
  const release = releases.get(viewportIndex);
  if (!release) return;
  releases.delete(viewportIndex);
  release();
};

/**
 * Lift every lock now, for the same reason: exiting a protocol, hanging a new
 * one and picking a layout by hand all rewrite several viewports' cameras before
 * React has unmounted anything.
 */
export const releaseAllAlignmentLocks = () => {
  [...releases.keys()].forEach(releaseAlignmentLock);
};

/**
 * Start holding `viewportIndex` at `alignment`, from wherever the image is now.
 *
 * The caller aligns first: this only remembers the resting place, so a viewport
 * locked before its alignment was written would be pinned to the wrong spot.
 *
 * @returns a function that lifts the lock, or null if there was nothing to hold
 *          (a centred viewport, or one cornerstone has not built yet).
 */
export const startAlignmentLock = async (viewportIndex, alignment) => {
  const direction = alignmentPanDirection(alignment);
  if (!direction) return null;

  let viewport;
  try {
    viewport = await getViewport(viewportIndex);
  } catch (error) {
    console.warn("Alignment lock: no viewport to lock", viewportIndex, error);
    return null;
  }

  const element = viewport?.element;
  if (!element || typeof viewport.getPan !== "function") return null;

  // Only the horizontal coordinate: the lock is about which side of the viewport
  // the image sits on, so the vertical pan is the radiologist's to move.
  const anchor = {
    x: viewport.getPan()[0],
    parallelScale: viewport.getCamera().parallelScale,
    width: element.clientWidth,
    height: element.clientHeight,
  };

  // setPan below re-fires CAMERA_MODIFIED; without this the correction would
  // recurse until the stack gave out.
  let repinning = false;

  /**
   * Take the camera as it stands to be the resting place.
   *
   * Used whenever cornerstone has worked out the alignment itself rather than
   * something having moved the image: it re-applies the stored displayArea on a
   * resize, on resetCamera, and on setOptions, and setDisplayArea does that with
   * camera events suppressed and from the fit camera - so the picture is already
   * flush against its edge and the only wrong number left is the remembered one.
   */
  const reanchor = () => {
    anchor.x = viewport.getPan()[0];
    anchor.parallelScale = viewport.getCamera().parallelScale;
    anchor.width = element.clientWidth;
    anchor.height = element.clientHeight;
  };

  const holdPosition = () => {
    if (repinning) return;
    try {
      const { clientWidth, clientHeight } = element;
      if (clientWidth !== anchor.width || clientHeight !== anchor.height) {
        // Backstop for a resize that did not come through setDisplayArea.
        reanchor();
        return;
      }

      const { parallelScale } = viewport.getCamera();
      if (
        Number.isFinite(parallelScale) &&
        parallelScale > 0 &&
        parallelScale !== anchor.parallelScale
      ) {
        const zoomApplied = anchor.parallelScale / parallelScale;
        anchor.x += direction * (clientWidth / 2) * (zoomApplied - 1);
        anchor.parallelScale = parallelScale;
      }

      const [panX, panY] = viewport.getPan();
      if (Math.abs(panX - anchor.x) < PAN_EPSILON) return;

      // panY passed through untouched, so a drag that moved the image both ways
      // keeps its vertical travel and loses only the sideways part.
      repinning = true;
      viewport.setPan([anchor.x, panY]);
      viewport.render();
    } catch (error) {
      console.warn(
        "Alignment lock: could not hold the viewport position",
        viewportIndex,
        error
      );
    } finally {
      repinning = false;
    }
  };

  // Re-locking the same viewport replaces rather than stacks: two listeners on
  // one element would fight over their two anchors.
  releaseAlignmentLock(viewportIndex);
  element.addEventListener(Enums.Events.CAMERA_MODIFIED, holdPosition);
  element.addEventListener(Enums.Events.DISPLAY_AREA_MODIFIED, reanchor);

  const release = () => {
    element.removeEventListener(Enums.Events.CAMERA_MODIFIED, holdPosition);
    element.removeEventListener(Enums.Events.DISPLAY_AREA_MODIFIED, reanchor);
  };
  releases.set(viewportIndex, release);

  return () => {
    if (releases.get(viewportIndex) === release) releases.delete(viewportIndex);
    release();
  };
};
